import React, { useEffect, useState } from 'react';

const VARIANTS = [
  {
    lead: 'Where homeowners',
    tail: 'hire pros',
    accent: '#0F172A',
    targetId: 'homeowner-card',
    testid: 'tagline-pill-homeowner',
  },
  {
    lead: 'Where contractors',
    tail: 'find leads',
    accent: '#D95800',
    targetId: 'contractor-card',
    testid: 'tagline-pill-contractor',
  },
  {
    lead: 'Where workers',
    tail: 'find jobs',
    accent: '#10B981',
    targetId: 'worker-card',
    testid: 'tagline-pill-worker',
  },
];

/**
 * Slowly rotates through three audience-specific taglines.
 * The accent (tail) word is a clickable pill that scrolls to the matching
 * audience card and plays a short attention pulse on it.
 * Honours prefers-reduced-motion: shows the full sentence with three pills inline.
 */
export const RotatingTagline = () => {
  const [idx, setIdx] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % VARIANTS.length);
    }, 3500);
    return () => clearInterval(id);
  }, [reduced]);

  const handlePillClick = (targetId) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Brief attention pulse
    el.classList.add('tagline-pulse');
    window.setTimeout(() => el.classList.remove('tagline-pulse'), 1800);
  };

  const Pill = ({ v, extraClass = '' }) => (
    <button
      type="button"
      onClick={() => handlePillClick(v.targetId)}
      data-testid={v.testid}
      className={`font-black uppercase tracking-wide px-2 py-0.5 border-2 border-black text-white hover:translate-y-[-1px] hover:shadow-[3px_3px_0px_#000] active:translate-y-0 active:shadow-none transition-all cursor-pointer ${extraClass}`}
      style={{ backgroundColor: v.accent }}
      aria-label={`Jump to ${v.lead.replace('Where ', '')} ${v.tail}`}
    >
      {v.tail}
    </button>
  );

  if (reduced) {
    // Static accessible variant — all three pills visible inline.
    return (
      <p
        className="text-lg md:text-xl text-white max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-x-2 gap-y-2"
        data-testid="rotating-tagline"
      >
        <span>Where homeowners</span>
        <Pill v={VARIANTS[0]} />
        <span>·</span>
        <span>contractors</span>
        <Pill v={VARIANTS[1]} />
        <span>·</span>
        <span>workers</span>
        <Pill v={VARIANTS[2]} />
      </p>
    );
  }

  const current = VARIANTS[idx];

  return (
    <div
      className="relative h-8 md:h-9 overflow-hidden max-w-2xl mx-auto"
      data-testid="rotating-tagline"
      aria-live="polite"
    >
      {VARIANTS.map((v, i) => {
        const isActive = i === idx;
        return (
          <p
            key={i}
            className={`absolute inset-0 flex items-center justify-center gap-2 text-lg md:text-xl text-white transition-all duration-500 ${
              isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
            }`}
          >
            <span className="font-medium">{v.lead}</span>
            <Pill v={v} />
          </p>
        );
      })}
      <span className="sr-only">{`${current.lead} ${current.tail}`}</span>
    </div>
  );
};

export default RotatingTagline;
